/**
 * Base E-commerce Tracker Class
 * All platform-specific trackers extend this class
 */
export default class BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    this.b2mTracker = b2mTracker;
    this.platformName = platformName;
    this.isInitialized = false;
    this.observedElements = new Set();
    this.dataLayerObserver = null;

    // Where the last add-to-cart interaction happened ({ source, ts }).
    // See Config.addToCartContextSelectors.
    this._lastAddToCartContext = null;
    
    // Standard e-commerce events
    this.eventTypes = {
      VIEW_ITEM: 'view_item',
      VIEW_ITEM_LIST: 'view_item_list',
      SELECT_ITEM: 'select_item',
      ADD_TO_CART: 'add_to_cart',
      REMOVE_FROM_CART: 'remove_from_cart',
      VIEW_CART: 'view_cart',
      BEGIN_CHECKOUT: 'begin_checkout',
      ADD_SHIPPING_INFO: 'add_shipping_info',
      ADD_PAYMENT_INFO: 'add_payment_info',
      PURCHASE: 'purchase',
      REFUND: 'refund',
      ADD_TO_WISHLIST: 'add_to_wishlist',
      REMOVE_FROM_WISHLIST: 'remove_from_wishlist',
      SEARCH: 'search',
      VIEW_PROMOTION: 'view_promotion',
      SELECT_PROMOTION: 'select_promotion'
    };
  }

  /**
   * Initialize the tracker - override in subclasses
   */
  init() {
    if (this.isInitialized) return;
    
    console.log(`[B2M ${this.platformName}] Initializing tracker...`);
    
    // Setup common tracking
    this.setupDataLayerObserver();
    this.setupAddToCartContextTracking();
    this.setupDOMObserver();
    this.detectCurrentPage();
    
    this.isInitialized = true;
    console.log(`[B2M ${this.platformName}] ✅ Tracker initialized`);
  }

  /**
   * SDK config (null-safe — unit tests may build a tracker without one).
   * @returns {object|null}
   */
  _sdkConfig() {
    return (this.b2mTracker && this.b2mTracker.tracker && this.b2mTracker.tracker.config) || null;
  }

  /**
   * Resolve which add-to-cart context a clicked element belongs to
   * (popup / recommender / product_detail / list / …), using the ordered
   * selector map from Config.addToCartContextSelectors.
   * @param {Element} target
   * @returns {string|null}
   */
  resolveAddToCartContext(target) {
    const cfg = this._sdkConfig();
    const map = cfg && cfg.addToCartContextSelectors;
    if (!map || !target || typeof target.closest !== 'function') return null;
    const ignore = cfg.addToCartContextIgnoreSelectors;
    if (ignore) {
      try { if (target.closest(ignore)) return null; } catch (e) { /* invalid selector */ }
    }
    for (const key of Object.keys(map)) {
      const selector = map[key];
      if (!selector) continue;
      try {
        if (target.closest(selector)) return key;
      } catch (e) { /* invalid selector — skip this entry */ }
    }
    return null;
  }

  /**
   * Remember the DOM context of every click so that an add_to_cart arriving
   * later (dataLayer push after the cart API responds) can be attributed to
   * the surface it came from — inline product card vs. variant popup on the
   * same listing URL. Capture phase: the site may stopPropagation on bubble.
   */
  setupAddToCartContextTracking() {
    if (typeof document === 'undefined' || !document.addEventListener) return;
    document.addEventListener('click', (event) => {
      const source = this.resolveAddToCartContext(event && event.target);
      if (source) this._lastAddToCartContext = { source, ts: Date.now() };
    }, true);
  }

  /**
   * Source for the add_to_cart being tracked right now: the last remembered
   * click context if it is fresh, otherwise a page-type fallback.
   * @returns {string} popup | recommender | product_detail | list | unknown | …
   */
  getAddToCartSource(pageTypeHint = null) {
    const cfg = this._sdkConfig();
    const ttl = (cfg && Number.isFinite(cfg.addToCartContextTtlMs)) ? cfg.addToCartContextTtlMs : 15000;
    const last = this._lastAddToCartContext;
    if (last && (Date.now() - last.ts) <= ttl) return last.source;

    // No fresh click: the site's own pageType on the dataLayer push is the
    // most reliable hint, then URL/DOM page-type detection.
    const fromHint = this._pageTypeToSource(pageTypeHint);
    if (fromHint) return fromHint;
    let pageType = null;
    try {
      if (typeof this.getPageType === 'function' && typeof window !== 'undefined' && window.location) {
        pageType = this.getPageType(window.location.pathname);
      }
    } catch (e) { /* ignore */ }
    return this._pageTypeToSource(pageType) || 'unknown';
  }

  /** product / pdp → product_detail; category / search / listing → list. */
  _pageTypeToSource(pageType) {
    if (!pageType || typeof pageType !== 'string') return null;
    const t = pageType.toLowerCase();
    if (t === 'product' || t === 'pdp' || t === 'product_detail' || t === 'productdetail') return 'product_detail';
    if (t === 'category' || t === 'search' || t === 'listing' || t === 'list' || t === 'plp' || t === 'productlist' || t === 'home' || t === 'homepage') return 'list';
    if (t === 'cart' || t === 'basket') return 'cart';
    return null;
  }

  /**
   * Setup dataLayer observer for GTM/GA events
   */
  setupDataLayerObserver() {
    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || [];
    
    // Store original push method
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    
    // Override push to intercept events
    window.dataLayer.push = (...args) => {
      // Process each pushed item
      args.forEach(item => {
        this.processDataLayerEvent(item);
      });
      
      // Call original push
      return originalPush(...args);
    };
    
    // Process existing dataLayer items
    window.dataLayer.forEach(item => {
      this.processDataLayerEvent(item);
    });
    
    console.log(`[B2M ${this.platformName}] DataLayer observer active`);
  }

  /**
   * Process dataLayer events - override in subclasses for platform-specific handling
   */
  processDataLayerEvent(data) {
    if (!data || typeof data !== 'object') return;
    
    // Handle standard GA4 e-commerce events
    if (data.event) {
      this.handleStandardEvent(data);
    }
    
    // Handle ecommerce object (enhanced ecommerce)
    if (data.ecommerce) {
      this.handleEnhancedEcommerceEvent(data);
    }
  }

  /**
   * Handle standard GA4 events
   */
  handleStandardEvent(data) {
    const eventName = data.event;
    const ecommerce = data.ecommerce || {};
    
    switch (eventName) {
      case 'view_item':
        this.trackViewItem(ecommerce.items || []);
        break;
      case 'view_item_list':
        this.trackViewItemList(ecommerce.items || [], ecommerce.item_list_name);
        break;
      case 'select_item':
        this.trackSelectItem(ecommerce.items || []);
        break;
      case 'add_to_cart':
        this.trackAddToCart(ecommerce.items || [], ecommerce.value, ecommerce.currency, {
          item_list_name: ecommerce.item_list_name,
          item_list_id: ecommerce.item_list_id,
          page_type: data.pageType || data.page_type,
          // The site's own verdict on where the add happened beats detection.
          add_to_cart_source: data.add_to_cart_source || data.page_field,
          page_field: data.page_field
        });
        break;
      case 'remove_from_cart':
        this.trackRemoveFromCart(ecommerce.items || []);
        break;
      case 'view_cart':
        this.trackViewCart(ecommerce.items || [], ecommerce.value, ecommerce.currency);
        break;
      case 'begin_checkout':
        this.trackBeginCheckout(ecommerce.items || [], ecommerce.value, ecommerce.currency);
        break;
      case 'add_shipping_info':
        this.trackAddShippingInfo(ecommerce);
        break;
      case 'add_payment_info':
        this.trackAddPaymentInfo(ecommerce);
        break;
      case 'purchase':
        this.trackPurchase(ecommerce);
        break;
      case 'refund':
        this.trackRefund(ecommerce);
        break;
    }
  }

  /**
   * Handle enhanced ecommerce events (legacy format)
   */
  handleEnhancedEcommerceEvent(data) {
    const ecommerce = data.ecommerce;
    
    if (ecommerce.impressions) {
      this.trackViewItemList(this.normalizeItems(ecommerce.impressions));
    }
    if (ecommerce.detail?.products) {
      this.trackViewItem(this.normalizeItems(ecommerce.detail.products));
    }
    if (ecommerce.click?.products) {
      this.trackSelectItem(this.normalizeItems(ecommerce.click.products));
    }
    if (ecommerce.add?.products) {
      // Enhanced Ecommerce (e.g. SAP Commerce / Spartacus GTM collector):
      // { event, pageType, ecommerce: { currencyCode, add: { actionField: { list }, products: [...] } } }
      this.trackAddToCart(this.normalizeItems(ecommerce.add.products), null, ecommerce.currencyCode || 'TRY', {
        item_list_name: ecommerce.add.actionField?.list,
        page_type: data.pageType || data.page_type,
        // The site's own verdict on where the add happened beats detection.
        add_to_cart_source: data.add_to_cart_source || data.page_field,
        page_field: data.page_field
      });
    }
    if (ecommerce.remove?.products) {
      this.trackRemoveFromCart(this.normalizeItems(ecommerce.remove.products));
    }
    if (ecommerce.checkout?.products) {
      this.trackBeginCheckout(this.normalizeItems(ecommerce.checkout.products));
    }
    if (ecommerce.purchase?.products) {
      this.trackPurchase({
        transaction_id: ecommerce.purchase.actionField?.id,
        value: ecommerce.purchase.actionField?.revenue,
        currency: ecommerce.currencyCode || 'TRY',
        items: this.normalizeItems(ecommerce.purchase.products)
      });
    }
  }

  /**
   * Setup DOM mutation observer for dynamic content
   */
  setupDOMObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.processNewElement(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Process newly added DOM elements - override in subclasses
   */
  processNewElement(element) {
    // Override in platform-specific trackers
  }

  /**
   * Detect current page type
   */
  detectCurrentPage() {
    const path = window.location.pathname;
    const pageType = this.getPageType(path);
    
    console.log(`[B2M ${this.platformName}] Page type: ${pageType}`);
    
    // Auto-track based on page type
    switch (pageType) {
      case 'product':
        this.autoTrackProductPage();
        break;
      case 'category':
        this.autoTrackCategoryPage();
        break;
      case 'cart':
        this.autoTrackCartPage();
        break;
      case 'checkout':
        this.autoTrackCheckoutPage();
        break;
      case 'order-confirmation':
        this.autoTrackOrderConfirmation();
        break;
      case 'search':
        this.autoTrackSearchPage();
        break;
    }
  }

  /**
   * Get page type from URL - override in subclasses
   */
  getPageType(path) {
    // Default patterns - override in platform-specific trackers
    if (path.includes('/product') || path.includes('/urun')) return 'product';
    if (path.includes('/category') || path.includes('/kategori')) return 'category';
    if (path.includes('/cart') || path.includes('/sepet')) return 'cart';
    if (path.includes('/checkout') || path.includes('/odeme')) return 'checkout';
    if (path.includes('/order-confirmation') || path.includes('/siparis-onay')) return 'order-confirmation';
    if (path.includes('/search') || path.includes('/arama')) return 'search';
    return 'other';
  }

  // ============================================
  // Standard E-commerce Tracking Methods
  // ============================================

  trackViewItem(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    const item = items[0]; // Usually single item on product page
    
    this.b2mTracker.track(this.eventTypes.VIEW_ITEM, {
      ecommerce_platform: this.platformName,
      item_id: item.item_id || item.id,
      item_name: item.item_name || item.name,
      item_brand: item.item_brand || item.brand,
      item_category: item.item_category || item.category,
      item_variant: item.item_variant || item.variant,
      price: item.price,
      currency: item.currency || 'TRY',
      ...extraData
    });
  }

  trackViewItemList(items, listName = '', extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.VIEW_ITEM_LIST, {
      ecommerce_platform: this.platformName,
      item_list_name: listName,
      items: items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        item_brand: item.item_brand || item.brand,
        item_category: item.item_category || item.category,
        price: item.price,
        index: item.index
      })),
      item_count: items.length,
      ...extraData
    });
  }

  trackSelectItem(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    const item = items[0];
    
    this.b2mTracker.track(this.eventTypes.SELECT_ITEM, {
      ecommerce_platform: this.platformName,
      item_id: item.item_id || item.id,
      item_name: item.item_name || item.name,
      item_list_name: item.item_list_name,
      index: item.index,
      ...extraData
    });
  }

  trackAddToCart(items, value = null, currency = 'TRY', extraData = {}) {
    if (!items || items.length === 0) return;
    
    const totalValue = value || items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

    // Drop undefined extras so they don't become empty event params.
    const extras = {};
    Object.keys(extraData || {}).forEach((k) => {
      if (extraData[k] !== undefined && extraData[k] !== null && extraData[k] !== '') extras[k] = extraData[k];
    });

    // List context: explicit (actionField.list / GA4 item_list_name) or the
    // first item's own list field.
    const itemWithList = items.find(item => item.item_list_name || item.list);
    if (!extras.item_list_name && itemWithList) {
      extras.item_list_name = itemWithList.item_list_name || itemWithList.list;
    }
    
    this.b2mTracker.track(this.eventTypes.ADD_TO_CART, {
      ecommerce_platform: this.platformName,
      items: items.map((item, i) => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        item_brand: item.item_brand || item.brand,
        item_category: item.item_category || item.category,
        item_variant: item.item_variant || item.variant,
        item_list_name: item.item_list_name || item.list,
        item_list_id: item.item_list_id,
        index: item.index ?? item.position ?? i,
        price: item.price,
        quantity: item.quantity || 1
      })),
      value: totalValue,
      currency: currency,
      // Where the add happened: popup / list / product_detail / recommender …
      // Site-provided value wins; otherwise SDK detection, flagged with an
      // underscore key (never sent) so a site-side enrichment that runs later
      // (e.g. a GTM tag adding page_field) can still take precedence.
      add_to_cart_source: extras.add_to_cart_source || this.getAddToCartSource(extras.page_type),
      ...(extras.add_to_cart_source ? {} : { _add_to_cart_source_detected: true }),
      ...extras
    });
  }

  trackRemoveFromCart(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.REMOVE_FROM_CART, {
      ecommerce_platform: this.platformName,
      items: items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        price: item.price,
        quantity: item.quantity || 1
      })),
      ...extraData
    });
  }

  trackViewCart(items, value, currency = 'TRY', extraData = {}) {
    this.b2mTracker.track(this.eventTypes.VIEW_CART, {
      ecommerce_platform: this.platformName,
      items: items,
      value: value,
      currency: currency,
      item_count: items.length,
      ...extraData
    });
  }

  trackBeginCheckout(items, value, currency = 'TRY', extraData = {}) {
    this.b2mTracker.track(this.eventTypes.BEGIN_CHECKOUT, {
      ecommerce_platform: this.platformName,
      items: items,
      value: value,
      currency: currency,
      ...extraData
    });
  }

  trackAddShippingInfo(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.ADD_SHIPPING_INFO, {
      ecommerce_platform: this.platformName,
      shipping_tier: data.shipping_tier,
      value: data.value,
      currency: data.currency || 'TRY',
      ...extraData
    });
  }

  trackAddPaymentInfo(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.ADD_PAYMENT_INFO, {
      ecommerce_platform: this.platformName,
      payment_type: data.payment_type,
      value: data.value,
      currency: data.currency || 'TRY',
      ...extraData
    });
  }

  trackPurchase(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.PURCHASE, {
      ecommerce_platform: this.platformName,
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency || 'TRY',
      tax: data.tax,
      shipping: data.shipping,
      coupon: data.coupon,
      items: data.items,
      ...extraData
    });
  }

  trackRefund(data, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.REFUND, {
      ecommerce_platform: this.platformName,
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency || 'TRY',
      items: data.items,
      ...extraData
    });
  }

  trackAddToWishlist(items, extraData = {}) {
    if (!items || items.length === 0) return;
    
    this.b2mTracker.track(this.eventTypes.ADD_TO_WISHLIST, {
      ecommerce_platform: this.platformName,
      items: items,
      ...extraData
    });
  }

  trackSearch(searchTerm, extraData = {}) {
    this.b2mTracker.track(this.eventTypes.SEARCH, {
      ecommerce_platform: this.platformName,
      search_term: searchTerm,
      ...extraData
    });
  }

  // ============================================
  // Auto-tracking methods - override in subclasses
  // ============================================

  autoTrackProductPage() {
    // Override in platform-specific trackers
  }

  autoTrackCategoryPage() {
    // Override in platform-specific trackers
  }

  autoTrackCartPage() {
    // Override in platform-specific trackers
  }

  autoTrackCheckoutPage() {
    // Override in platform-specific trackers
  }

  autoTrackOrderConfirmation() {
    // Override in platform-specific trackers
  }

  autoTrackSearchPage() {
    // Override in platform-specific trackers
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Normalize items from different formats to GA4 format
   */
  normalizeItems(items) {
    if (!items) return [];
    
    return items.map((item, index) => ({
      item_id: item.id || item.item_id || item.product_id || item.sku,
      item_name: item.name || item.item_name || item.product_name || item.title,
      item_brand: item.brand || item.item_brand || item.vendor,
      item_category: item.category || item.item_category || item.product_type,
      item_variant: item.variant || item.item_variant,
      // Enhanced Ecommerce `list` / GA4 `item_list_name` — the list the
      // product was found in (category, carousel, search …).
      item_list_name: item.item_list_name || item.list,
      item_list_id: item.item_list_id,
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1,
      index: item.position ?? item.index ?? index
    }));
  }

  /**
   * Extract product data from DOM element
   */
  extractProductFromElement(element) {
    // Try various data attributes
    const product = {
      item_id: element.dataset.productId || element.dataset.id || element.dataset.sku,
      item_name: element.dataset.productName || element.dataset.name || element.dataset.title,
      price: parseFloat(element.dataset.price || element.dataset.productPrice) || 0,
      item_brand: element.dataset.brand || element.dataset.productBrand,
      item_category: element.dataset.category || element.dataset.productCategory,
      item_variant: element.dataset.variant || element.dataset.productVariant,
      quantity: parseInt(element.dataset.quantity) || 1
    };
    
    // If no data attributes, try to extract from child elements
    if (!product.item_name) {
      const nameEl = element.querySelector('.product-name, .product-title, [class*="name"], [class*="title"]');
      if (nameEl) product.item_name = nameEl.textContent.trim();
    }
    
    if (!product.price) {
      const priceEl = element.querySelector('.price, .product-price, [class*="price"]');
      if (priceEl) {
        const priceText = priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.');
        product.price = parseFloat(priceText) || 0;
      }
    }
    
    return product;
  }

  /**
   * Parse price string to number
   */
  parsePrice(priceStr) {
    if (typeof priceStr === 'number') return priceStr;
    if (!priceStr) return 0;
    
    // Remove currency symbols and whitespace
    const cleaned = priceStr.toString()
      .replace(/[^\d.,]/g, '')
      .replace(/\.(?=.*\.)/g, '') // Keep only last dot
      .replace(',', '.');
    
    return parseFloat(cleaned) || 0;
  }
}
